import React, { useState, useEffect, useRef } from 'react';
import { revealTransition } from '../revealTransition';

export const THEMES = [
  { id: 'beige', name: 'Beige', swatch: '#e7e5e4', ring: '#a8a29e' },
  { id: 'dark', name: 'Dark', swatch: '#1c1917', ring: '#78716c' },
  { id: 'midnight', name: 'Midnight Blue', swatch: '#0f172a', ring: '#3b5b8f' },
  { id: 'solarized', name: 'Solarized', swatch: '#fdf6e3', ring: '#b58900' },
];

const STORAGE_KEY = 'devtools-theme';

export const applyTheme = (themeId) => {
  if (themeId === 'beige') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', themeId);
  }
};

const ThemeSwitcher = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem(STORAGE_KEY) || 'beige');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const switchTheme = (id, event) => {
    setOpen(false);
    if (id === theme) return;

    const commit = () => {
      setTheme(id);
      applyTheme(id);
    };

    revealTransition(event.clientX, event.clientY, commit, () => {
      document.documentElement.classList.add('theme-fade');
      commit();
      setTimeout(() => document.documentElement.classList.remove('theme-fade'), 700);
    });
  };

  const current = THEMES.find((t) => t.id === theme) || THEMES[0];

  return (
    <div ref={ref} className="fixed top-4 right-4 z-50">
      <div
        data-testid="theme-switcher-menu"
        className={`theme-pill flex items-center gap-2 bg-white border border-gray-300 rounded-full shadow-md px-2 py-2 ${open ? 'theme-pill-open' : ''}`}
      >
        {open &&
          THEMES.map((t, i) => (
            <button
              key={t.id}
              data-testid={`theme-option-${t.id}`}
              title={t.name}
              onClick={(e) => switchTheme(t.id, e)}
              className="theme-swatch relative w-7 h-7 rounded-full flex-shrink-0 focus:outline-none"
              style={{
                backgroundColor: t.swatch,
                animationDelay: `${i * 60}ms`,
                boxShadow:
                  theme === t.id
                    ? `0 0 0 2px var(--bg-page, #ffffff), 0 0 0 4px ${t.ring}`
                    : `inset 0 0 0 1px ${t.ring}55`,
              }}
            >
              {theme === t.id && (
                <svg
                  className="absolute inset-0 m-auto w-4 h-4"
                  style={{ color: t.id === 'beige' || t.id === 'solarized' ? '#57534e' : '#e7e5e4' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}

        <button
          data-testid="theme-switcher-button"
          onClick={() => setOpen(!open)}
          title={open ? 'Close theme picker' : `Theme: ${current.name}`}
          className={`theme-toggle relative w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-gray-700 hover:text-gray-900 focus:outline-none ${open ? 'theme-toggle-open' : ''}`}
        >
          {open ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485" />
              </svg>
              <span
                className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-gray-300"
                style={{ backgroundColor: current.swatch }}
              />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ThemeSwitcher;
